import { __ } from '../functions/commom';
import { useAppState } from '../state/StateContext';
import moment from 'moment-timezone';
import Box from './Box';
import { getInsuranceCalculate } from '../functions/insuranceSearchService';
import { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { InsuranceQuote } from '../types/insurance.types';
import Tab from './Tab';
import SearchFormTravel from './SearchFormTravel';
import InsuranceTierList from './InsuranceTierList';
import SearchFormHome from './SearchFormHome';
import { DATE_FORMAT, MIN_ADVANCE_HOURS, TIMEZONE } from '../config';
import { useVisibility } from '../hooks/useVisibility';
import { createUseStyles } from 'react-jss';

const useStyles = createUseStyles({
  errorMessage: {
    backgroundColor: '#ffebee',
    color: '#c62828',
    padding: '12px 16px',
    borderRadius: '8px',
    marginBottom: '16px',
    fontSize: '14px',
    border: '1px solid #ef9a9a',
  },
});

const Search: React.FC = () => {
  const classes = useStyles();

  // Get URL parameters
  const { category: urlCategory = 'travel', id } = useParams<{
    category?: string;
    id?: string;
  }>();

  const [searchParams] = useSearchParams();
  const state = useAppState();
  const { visibility, loading: visibilityLoading } = useVisibility();

  const [activedTab, setActivedTab] = useState(urlCategory || 'travel');

  // Use state to track the current category and id
  const [category, setCategory] = useState(urlCategory);
  const [quoteList, setQuoteList] = useState<InsuranceQuote | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Calculate minimum booking date (48 hours from now) - memoized to prevent recalculation
  const minBookingDate = useMemo(() => {
    return moment().tz(TIMEZONE).add(MIN_ADVANCE_HOURS, 'hours').format(DATE_FORMAT);
  }, []);

  const couponCode =
    searchParams?.get('couponCode') ||
    state?.searchForm?.couponCode?.get() ||
    '';

  const coverageType =
    searchParams?.get('coverageType') ||
    state?.searchForm?.coverageType?.get() ||
    'individual';

  function handleCategoryChange(value: string | undefined) {
    // Use React Router's navigate instead of window.location.href
    setCategory(value || 'travel');
    setActivedTab(value || 'travel');
    setQuoteList(null);
    setErrorMessage(null);
  }

  const handleCalculate = useCallback(async () => {
    try {
      setErrorMessage(null);
      const result = await getInsuranceCalculate(state.searchForm.get());
      setQuoteList(result);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'An error occurred';
      setErrorMessage(message);
      setQuoteList(null);
      console.error('Calculate error:', message);
    }
  }, [state.searchForm]);

  // init search form with minimum booking date (48 hours in advance)
  useEffect(() => {
    setErrorMessage(null);
    state.searchForm.set(
      activedTab === 'travel'
        ? {
            id: 'single_trip',
            departureDate: minBookingDate,
            returnDate: moment(minBookingDate).add(2, 'day').format(DATE_FORMAT),
            coverageType: coverageType || 'individual',
            category: 'travel',
            withCruise: false,
            couponCode: couponCode,
          }
        : {
            id: 'goal_home_comprehensive_insurance',
            departureDate: minBookingDate,
            coverageType: 'individual',
            category: 'home',
            buildingAge: 25,
            floorArea: 500,
            areaType: 'net',
            couponCode: couponCode,
          }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activedTab, minBookingDate]);

  useEffect(() => {
    state.topbar.title.set(__('Select your plan and tier'));
  }, [id, state.topbar.title]);

  // Handle visibility changes - auto-switch to available tab if current tab is hidden
  useEffect(() => {
    if (!visibilityLoading && visibility) {
      const isCurrentTabVisible =
        visibility[activedTab as keyof typeof visibility];

      if (!isCurrentTabVisible) {
        // Find the first available tab
        if (visibility.travel) {
          handleCategoryChange('travel');
        } else if (visibility.home) {
          handleCategoryChange('home');
        }
        // If no tabs are visible, the UI will show the "no categories available" message
      }
    }
  }, [visibility, visibilityLoading, activedTab]);

  useEffect(() => {
    setQuoteList(null);
    const timeoutId = setTimeout(() => {
      handleCalculate();
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [id, state?.searchForm, category, handleCalculate]);

  return (
    <>
      <div className="row">
        <div className="col-md-7 col-xs-12">
          <Box>
            {/* Display error message if any */}
            {errorMessage && (
              <div className={classes.errorMessage}>
                {__(errorMessage)}
              </div>
            )}
            {!visibilityLoading && visibility && (
              <>
                {/* Show tabs only if more than one category is visible */}
                {visibility.travel && visibility.home ? (
                  <Tab
                    activeTabId={activedTab}
                    sets={[
                      ...(visibility.travel
                        ? [
                            {
                              id: 'travel',
                              title: __('Travel'),
                              content: <SearchFormTravel />,
                              onTitleClick: () =>
                                handleCategoryChange('travel'),
                            },
                          ]
                        : []),
                      ...(visibility.home
                        ? [
                            {
                              id: 'home',
                              title: __('Home'),
                              content: <SearchFormHome />,
                              onTitleClick: () => handleCategoryChange('home'),
                            },
                          ]
                        : []),
                    ]}
                  />
                ) : (
                  /* Show content directly without tabs when only one category is visible */
                  <>
                    {visibility.travel && <SearchFormTravel />}
                    {visibility.home && <SearchFormHome />}
                  </>
                )}
              </>
            )}
            {visibilityLoading && (
              <div style={{ padding: '20px', textAlign: 'center' }}>
                {__('Loading...')}
              </div>
            )}
            {!visibilityLoading &&
              visibility &&
              !visibility.travel &&
              !visibility.home && (
                <div style={{ padding: '20px', textAlign: 'center' }}>
                  {__('No insurance categories are currently available.')}
                </div>
              )}

            <div className="reminder">
              {__('All currencies are quoted in Pataca de Macau (MOP).')} <br />
              {__(
                'Before purchasing this insurance, please ensure you fully understand its features and that it meets your needs.'
              )}
            </div>
          </Box>
        </div>
        <div className="col-md-5 col-xs-12">
          <InsuranceTierList quoteList={quoteList} />
        </div>
      </div>
    </>
  );
};

export default Search;
